import {useState, useMemo} from 'react'
import type {Board, TaskStatus} from '../../types'
import {useApp} from '../../context/AppContext'
import {generateId} from '../../utils/helpers'
import KanbanColumn from './KanbanColumn'
import NewTaskForm from './NewTaskForm'
import type {BoardFilters} from './BoardFilters'

interface KanbanBoardProps {
  board: Board
  filters?: BoardFilters
}

export default function KanbanBoard({board, filters}: KanbanBoardProps) {
  const {getTasksForBoard} = useApp()
  const [newTaskStatus, setNewTaskStatus] = useState<TaskStatus | null>(null)
  const allTasks = getTasksForBoard(board.id)

  const tasks = useMemo(() => {
    if (!filters) return allTasks

    return allTasks.filter((task) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        if (
          !task.title.toLowerCase().includes(searchLower) &&
          !task.description.toLowerCase().includes(searchLower)
        ) {
          return false
        }
      }

      if (filters.status.length > 0 && !filters.status.includes(task.status)) {
        return false
      }

      if (filters.priority.length > 0 && !filters.priority.includes(task.priority)) {
        return false
      }

      if (filters.assigneeIds.length > 0) {
        const hasAssignee = filters.assigneeIds.some((id) => task.assigneeIds.includes(id))
        if (!hasAssignee) return false
      }

      if (filters.labelIds.length > 0) {
        const hasLabel = filters.labelIds.some((id) => task.labelIds.includes(id))
        if (!hasLabel) return false
      }

      return true
    })
  }, [allTasks, filters])

  const handleAddTask = (status: TaskStatus) => {
    setNewTaskStatus(status)
  }

  return (
    <>
      <div className='kanban-scroll flex gap-4 h-full pb-4'>
        {board.columns.map((column) => {
          const columnTasks = tasks.filter((t) => t.status === column.status)
          return (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={columnTasks}
              onAddTask={handleAddTask}
            />
          )
        })}
      </div>

      {newTaskStatus && (
        <NewTaskForm
          boardId={board.id}
          initialStatus={newTaskStatus}
          onClose={() => setNewTaskStatus(null)}
        />
      )}
    </>
  )
}

export function QuickTaskId() {
  return `task-${generateId()}`
}
